import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiGet, apiPost, apiDelete, apiUpload, dataUrlToBlob } from '../api';
import { Parser, Player } from 'svga-web';
import { formatNumberPrecise } from '../Utils';
import VapPreview from './VapPreview';

const Store = () => {
  const [formData, setFormData] = useState({ name: '', price: '', type: 'frame' });
  const [svgaFile, setSvgaFile] = useState(null);
  const [vapFile, setVapFile] = useState(null);
  const [animType, setAnimType] = useState('svga'); // 'svga' | 'vap'
  const [storeItems, setStoreItems] = useState([]);
  const [filterType, setFilterType] = useState('');
  const [loading, setLoading] = useState(false);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [svgaPreviewData, setSvgaPreviewData] = useState(null);
  const previewCanvasRef = useRef(null);
  const previewPlayerRef = useRef(null);
  const [statusText, setStatusText] = useState('');

  const itemTypes = [{ value: 'frame', label: 'Frame' }, { value: 'entry', label: 'Entry' }];
  const filterOptions = [{ label: 'All', value: '' }, { label: 'Frame', value: 'frame' }, { label: 'Entry', value: 'entry' }];

  const fetchItems = useCallback(async () => {
    try {
      const items = await apiGet(`/api/store/all${filterType ? `?type=${filterType}` : ''}`);
      setStoreItems(items);
    } catch (error) {
      alert('Failed to load items');
    }
  }, [filterType]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  useEffect(() => {
    if (svgaPreviewData && previewCanvasRef.current) {
      if (previewPlayerRef.current) previewPlayerRef.current.destroy();
      const player = new Player(previewCanvasRef.current);
      player.mount(svgaPreviewData).then(() => { previewPlayerRef.current = player; player.start(); });
    }
    return () => { if (previewPlayerRef.current) { previewPlayerRef.current.destroy(); previewPlayerRef.current = null; } };
  }, [svgaPreviewData]);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSvgaFile(null); setVapFile(null); setThumbnailPreview(null); setSvgaPreviewData(null); setStatusText('');
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (lower.endsWith('.mp4')) {
      // Tencent VAP video frame/entry — preview + thumbnail via <VapPreview/>
      setAnimType('vap');
      setVapFile(file);
      setStatusText('Reading VAP file… generating transparent preview');
      return;
    }
    if (lower.endsWith('.svga')) {
      setAnimType('svga');
      setSvgaFile(file); setStatusText('Processing SVGA file...');
      const reader = new FileReader();
      reader.onload = async () => {
        let thumbnailPlayer = null;
        try {
          const buffer = reader.result;
          const parser = new Parser();
          const svgaData = await parser.do(buffer);
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = 200;
          tempCanvas.height = 200;
          thumbnailPlayer = new Player(tempCanvas);
          const totalFrames = svgaData.frames;
          if (!totalFrames) throw new Error("Could not get total frames.");
          const targetFrame = Math.max(1, Math.floor(totalFrames / 2));
          await thumbnailPlayer.mount(svgaData);
          thumbnailPlayer.set({ loop: 1, fillMode: 'forwards', startFrame: targetFrame - 1, endFrame: targetFrame });
          const getThumbnailPromise = new Promise((resolve, reject) => {
            thumbnailPlayer.$on('end', () => resolve(tempCanvas.toDataURL('image/webp', 0.75)));
            thumbnailPlayer.$on('error', reject);
            thumbnailPlayer.start();
          });
          const generatedThumbnail = await getThumbnailPromise;
          setThumbnailPreview(generatedThumbnail);
          setSvgaPreviewData(svgaData);
          setStatusText('Preview ready!');
        } catch (error) {
          setStatusText(`Error: ${error.message}`);
        } finally {
          if (thumbnailPlayer) thumbnailPlayer.destroy();
        }
      };
      reader.readAsArrayBuffer(file);
      return;
    }
    setStatusText('Invalid file. Select a .svga or .mp4 (VAP) file.');
  };

  const uploadGift = async () => {
    const { name, price, type } = formData;
    const animFile = animType === 'vap' ? vapFile : svgaFile;
    if (!name || !price || !animFile || !thumbnailPreview || !type) {
      alert('Please fill all fields and select an SVGA or VAP file.'); return;
    }
    setLoading(true); setStatusText('Uploading...');
    try {
      const { url: animUrl } = await apiUpload('store', animFile, `${animType}_${Date.now()}_${animFile.name}`);
      setStatusText('File uploaded. Uploading thumbnail...');
      const thumbBlob = dataUrlToBlob(thumbnailPreview);
      const baseName = animFile.name.replace(/\.(svga|mp4)$/i, '');
      const { url: thumbnailUrl } = await apiUpload('store', thumbBlob, `thumb_${Date.now()}_${baseName}.webp`);
      setStatusText('Thumbnail uploaded. Saving to database...');
      const payload = { name, price: parseFloat(price), type, thumbnailUrl, animType };
      if (animType === 'vap') payload.vapUrl = animUrl; else payload.svgaUrl = animUrl;
      await apiPost('/api/store', payload);
      setStatusText('Item uploaded successfully!');
      setFormData({ name: '', price: '', type: 'frame' });
      setSvgaFile(null); setVapFile(null); setAnimType('svga'); setThumbnailPreview(null); setSvgaPreviewData(null);
      if (document.getElementById('svga-input')) document.getElementById('svga-input').value = '';
      fetchItems();
    } catch (error) {
      alert('Failed to upload item');
      setStatusText(`Error: ${error.message}`);
    } finally {
      setLoading(false);
      setTimeout(() => setStatusText(''), 3000);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await apiDelete(`/api/store/${id}`);
      fetchItems();
    } catch (error) {
      alert('Failed to delete item');
    }
  };

  return (
    <div className="dark-page">
      {/* Upload Form */}
      <div className="dark-upload-card">
        <h3>Upload New Store Item 🛍️</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <div>
            <label htmlFor="name">Item Name</label>
            <input id="name" type="text" value={formData.name} onChange={handleInputChange} placeholder="Enter item name" />
            <label htmlFor="price">Item Price (Diamond)</label>
            <input id="price" type="number" value={formData.price} onChange={handleInputChange} placeholder="Enter price" />
            <label htmlFor="type">Item Type</label>
            <select id="type" value={formData.type} onChange={handleInputChange} style={{ width: '100%', padding: '10px 14px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#f1f5f9', fontSize: 14, marginBottom: 14 }}>
              {itemTypes.map(opt => <option key={opt.value} value={opt.value} style={{ background: '#1a0a2e' }}>{opt.label}</option>)}
            </select>
            <label>Animation File (SVGA or VAP .mp4)</label>
            <input id="svga-input" type="file" accept=".svga,.mp4" onChange={handleFileChange} />
          </div>
          <div className="preview-zone">
            {animType === 'vap' && vapFile ? (
              <div style={{ display: 'flex', justifyContent: 'space-around', width: '100%', gap: 12, alignItems: 'center' }}>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>Transparent preview <span style={{ color: '#a78bfa' }}>(VAP)</span></p>
                  <VapPreview
                    file={vapFile}
                    width={100}
                    height={160}
                    onReady={(cfg) => setStatusText(`VAP ready — ${cfg.rgbFrame[2]}×${cfg.rgbFrame[3]}`)}
                    onThumbnail={(d) => setThumbnailPreview(d)}
                    onError={(m) => setStatusText(m)}
                  />
                </div>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>Thumbnail</p>
                  {thumbnailPreview
                    ? <img src={thumbnailPreview} alt="thumbnail" style={{ width: 100, height: 100, objectFit: 'contain' }} />
                    : <div style={{ width: 100, height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>generating…</div>}
                </div>
              </div>
            ) : thumbnailPreview ? (
              <div style={{ display: 'flex', justifyContent: 'space-around', width: '100%', gap: 12 }}>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>Thumbnail</p>
                  <img src={thumbnailPreview} alt="thumbnail" style={{ width: 100, height: 100, objectFit: 'contain' }} />
                </div>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>Animation</p>
                  <canvas ref={previewCanvasRef} style={{ width: 100, height: 100 }} />
                  <button type="button" onClick={() => previewPlayerRef.current?.start()} style={{ marginTop: 6, fontSize: 11, color: '#a78bfa', background: 'none', border: 'none', cursor: 'pointer' }}>
                    ▶ Replay
                  </button>
                </div>
              </div>
            ) : (
              <>
                <i className="fas fa-image" style={{ fontSize: 36, marginBottom: 10, opacity: 0.3 }} />
                <p>Select an SVGA or VAP (.mp4) file to see preview</p>
              </>
            )}
            {statusText && <p className="status-blue" style={{ marginTop: 12 }}>{statusText}</p>}
          </div>
        </div>
        <div style={{ marginTop: 16, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 16 }}>
          <button className="btn-upload" onClick={uploadGift} disabled={loading || !thumbnailPreview}>
            {loading ? <><i className="fas fa-spinner fa-spin" /> Uploading...</> : <><i className="fas fa-upload" /> Upload Item</>}
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="dark-filter-bar">
        {filterOptions.map(opt => (
          <button
            key={opt.value}
            className={`filter-btn${filterType === opt.value ? ' active' : ''}`}
            onClick={() => setFilterType(opt.value)}
          >
            {opt.label}
            {filterType === opt.value && <i className="fas fa-check" style={{ marginLeft: 6 }} />}
          </button>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: 13, color: 'rgba(255,255,255,0.4)' }}>
          {storeItems.length} item{storeItems.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Items Grid */}
      <h4 className="section-heading">
        {filterType ? `${filterType.charAt(0).toUpperCase() + filterType.slice(1)} Items` : 'All Items'}
      </h4>
      {storeItems.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.3)' }}>
          <i className="fas fa-box-open" style={{ fontSize: 36, marginBottom: 10, display: 'block' }} />
          No items found. Upload some items to get started.
        </div>
      ) : (
        <div className="dark-grid">
          {storeItems.map(item => (
            <div key={item.id} className="dark-grid-item">
              <img src={item.thumbnailUrl} alt={item.name} />
              <div className="item-name" title={item.name}>{item.name}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
                <div className="item-price">{formatNumberPrecise(item.price)} 💎</div>
                <span className={`badge ${item.type === 'frame' ? 'badge-purple' : 'badge-blue'}`} style={{ fontSize: 10 }}>{item.type}</span>
                {(item.animType === 'vap' || item.vapUrl) && (
                  <span style={{ fontSize: 9, fontWeight: 700, color: '#fff', background: '#7c3aed', padding: '1px 5px', borderRadius: 4 }}>VAP</span>
                )}
              </div>
              <button className="delete-btn" onClick={() => handleDelete(item.id)} title="Delete">
                <i className="fas fa-times" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Store;
